import type { RegisterAccess } from "@shared/api";
import type { LocalStore } from "@client/db/contract";
import { apiFetch, setDeviceId } from "@client/auth";

/**
 * D-070: what a device does with a shared register once the server stops serving it, or once it drops off
 * the register list. The server's answer comes from D1, not the session cache, so it is current.
 *
 *  - `normal`: carry on (the device still has access, or the register is the device's own).
 *  - `readOnly`: joined, but the owner is not on Pro (D-068, D-071). Show what is stored, record nothing.
 *  - `remove`: the owner revoked this device, moved it to another register, or archived the register. The
 *    register and its local data are to be cleared from the device.
 *  - `ended`: this device's cashier session limit ran out (D-081). Cleared like `remove`, with its own notice.
 *  - `unknown`: the server could not be asked (offline). Leave things as they are.
 *
 * Only a register this device holds as a cashier is ever removed. An owner's own register is never cleared
 * here: device-local data survives even the retention purge (D-017).
 */
export type AccessOutcome = "normal" | "readOnly" | "remove" | "ended" | "unknown";

export async function checkRegisterAccess(
  registerId: string,
  store: LocalStore,
  fetchAccess: (registerId: string) => Promise<RegisterAccess> = defaultFetchAccess,
): Promise<{ outcome: AccessOutcome; name: string | null }> {
  let access: RegisterAccess["access"];
  try {
    // The answer is about one device, so it must be asked as the store's own device: a stale id from
    // localStorage would be answered `none` and could clear a register that is only suspended.
    setDeviceId((await store.getMeta()).deviceId);
    access = (await fetchAccess(registerId)).access;
  } catch {
    return { outcome: "unknown", name: null };
  }
  const local = (await store.localRegisters()).find((r) => r.id === registerId) ?? null;
  const name = local?.settings.name ?? null;
  if (access === "suspended") return { outcome: "readOnly", name };
  if (access === "none" && local?.role === "cashier") return { outcome: "remove", name };
  if (access === "ended" && local?.role === "cashier") return { outcome: "ended", name };
  return { outcome: "normal", name };
}

function defaultFetchAccess(registerId: string): Promise<RegisterAccess> {
  return apiFetch<RegisterAccess>(`/api/registers/${encodeURIComponent(registerId)}/access`);
}
